import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import Button from './Button';
import Spinner from './Spinner';

// Styled components
const FormContainer = styled.div`
  background: linear-gradient(135deg, #111 0%, #1a1a1a 100%);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 2rem;
  margin-top: 2rem;
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  color: #0d9488;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const FormSection = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #333;
  background: #000;
  color: #fff;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    border-color: #0d9488;
    outline: none;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }

  &::placeholder {
    color: #666;
  }

  &:disabled {
    background-color: #111;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #333;
  background: #000;
  color: #fff;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    border-color: #0d9488;
    outline: none;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }

  &:disabled {
    background-color: #111;
    cursor: not-allowed;
    opacity: 0.6;
  }

  option {
    background: #000;
    color: #fff;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem 1rem;
  border: 2px solid #333;
  background: #000;
  color: #fff;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    border-color: #0d9488;
    outline: none;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }

  &::placeholder {
    color: #666;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ccc;
  cursor: pointer;
  font-size: 0.9rem;

  input[type="checkbox"] {
    accent-color: #0d9488;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid #22c55e;
  color: #22c55e;
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoBox = styled.div`
  background: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  
  h4 {
    color: #0d9488;
    margin-bottom: 0.75rem;
    font-size: 1.1rem;
  }
  
  p {
    color: #ccc;
    line-height: 1.5;
    font-size: 0.95rem;
  }
`;

const WhatsAppInfo = styled.div`
  background: rgba(37, 211, 102, 0.1);
  border: 1px solid #25d366;
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;
  text-align: center;
  
  a {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #25d366;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

interface PropertyRequestFormProps {
  type: 'auctions' | 'buy-sell';
  onClose?: () => void;
}

const PropertyRequestForm: React.FC<PropertyRequestFormProps> = ({ type, onClose }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    requestType: type === 'auctions' ? 'buy' : 'buy', // buy, sell, info
    propertyType: '',
    location: '',
    budget: '',
    timeline: '',
    financing: [] as string[],
    experience: '',
    message: ''
  });

  const requestTypes = {
    auctions: {
      it: {
        title: 'Richiesta Informazioni Aste',
        types: [
          { value: 'buy', label: 'Voglio partecipare a un\'asta' },
          { value: 'info', label: 'Informazioni generali sulle aste' },
          { value: 'support', label: 'Assistenza per asta specifica' }
        ]
      },
      en: {
        title: 'Auction Information Request',
        types: [
          { value: 'buy', label: 'I want to participate in an auction' },
          { value: 'info', label: 'General information about auctions' },
          { value: 'support', label: 'Support for specific auction' }
        ]
      }
    },
    'buy-sell': {
      it: {
        title: 'Richiesta Compravendita',
        types: [
          { value: 'buy', label: 'Voglio comprare' },
          { value: 'sell', label: 'Voglio vendere' },
          { value: 'info', label: 'Consulenza immobiliare' }
        ]
      },
      en: {
        title: 'Buy & Sell Request',
        types: [
          { value: 'buy', label: 'I want to buy' },
          { value: 'sell', label: 'I want to sell' },
          { value: 'info', label: 'Real estate consulting' }
        ]
      }
    }
  };

  const propertyTypes = [
    { value: 'apartment', label: language === 'it' ? 'Appartamento' : 'Apartment' },
    { value: 'house', label: language === 'it' ? 'Casa/Villa' : 'House/Villa' },
    { value: 'commercial', label: language === 'it' ? 'Commerciale' : 'Commercial' },
    { value: 'land', label: language === 'it' ? 'Terreno' : 'Land' },
    { value: 'other', label: language === 'it' ? 'Altro' : 'Other' }
  ];

  const timelines = [
    { value: 'immediate', label: language === 'it' ? 'Immediato (entro 3 mesi)' : 'Immediate (within 3 months)' },
    { value: 'short', label: language === 'it' ? 'Breve termine (3-6 mesi)' : 'Short term (3-6 months)' },
    { value: 'medium', label: language === 'it' ? 'Medio termine (6-12 mesi)' : 'Medium term (6-12 months)' },
    { value: 'long', label: language === 'it' ? 'Lungo termine (oltre 12 mesi)' : 'Long term (over 12 months)' }
  ];

  const financingOptions = [
    { value: 'cash', label: language === 'it' ? 'Pagamento in contanti' : 'Cash payment' },
    { value: 'mortgage', label: language === 'it' ? 'Mutuo bancario' : 'Bank mortgage' },
    { value: 'mixed', label: language === 'it' ? 'Finanziamento misto' : 'Mixed financing' },
    { value: 'business', label: language === 'it' ? 'Finanziamento aziendale' : 'Business financing' }
  ];

  const experiences = [
    { value: 'first-time', label: language === 'it' ? 'Prima esperienza' : 'First time' },
    { value: 'some', label: language === 'it' ? 'Qualche esperienza' : 'Some experience' },
    { value: 'experienced', label: language === 'it' ? 'Esperto' : 'Experienced' },
    { value: 'professional', label: language === 'it' ? 'Professionista del settore' : 'Industry professional' }
  ];

  const currentConfig = requestTypes[type][language];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFinancingChange = (option: string) => {
    setFormData(prev => ({
      ...prev,
      financing: prev.financing.includes(option)
        ? prev.financing.filter(f => f !== option)
        : [...prev.financing, option]
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.clientName.trim()) {
      return language === 'it' ? 'Il nome è obbligatorio' : 'Name is required';
    }
    if (!formData.clientEmail.trim()) {
      return language === 'it' ? "L'email è obbligatoria" : 'Email is required';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)) {
      return language === 'it' ? 'Email non valida' : 'Invalid email';
    }
    if (!formData.requestType) {
      return language === 'it' ? 'Seleziona il tipo di richiesta' : 'Select request type';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Invia email di notifica
      const emailData = {
        to: 'admin@j-o-h.com',
        subject: `🏛️ Nuova richiesta ${type === 'auctions' ? 'Aste' : 'Compravendita'}: ${formData.clientName}`,
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #dc2626;">🏛️ Nuova Richiesta ${type === 'auctions' ? 'Aste' : 'Compravendita'}</h2>
            
            <div style="background: #fee2e2; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #dc2626;">⚠️ AZIONE RICHIESTA</h3>
              <p>È arrivata una nuova richiesta per ${type === 'auctions' ? 'servizi di asta' : 'compravendita immobiliare'}</p>
            </div>
            
            <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #0d9488;">Dettagli Cliente:</h3>
              <p><strong>Nome:</strong> ${formData.clientName}</p>
              <p><strong>Email:</strong> ${formData.clientEmail}</p>
              ${formData.clientPhone ? `<p><strong>Telefono:</strong> ${formData.clientPhone}</p>` : ''}
              
              <h4 style="color: #0d9488; margin-top: 20px;">Dettagli Richiesta:</h4>
              <p><strong>Tipo richiesta:</strong> ${currentConfig.types.find(t => t.value === formData.requestType)?.label}</p>
              ${formData.propertyType ? `<p><strong>Tipo immobile:</strong> ${propertyTypes.find(p => p.value === formData.propertyType)?.label}</p>` : ''}
              ${formData.location ? `<p><strong>Zona interesse:</strong> ${formData.location}</p>` : ''}
              ${formData.budget ? `<p><strong>Budget:</strong> ${formData.budget}</p>` : ''}
              ${formData.timeline ? `<p><strong>Tempistiche:</strong> ${timelines.find(t => t.value === formData.timeline)?.label}</p>` : ''}
              ${formData.financing.length > 0 ? `<p><strong>Finanziamento:</strong> ${formData.financing.map(f => financingOptions.find(opt => opt.value === f)?.label).join(', ')}</p>` : ''}
              ${formData.experience ? `<p><strong>Esperienza:</strong> ${experiences.find(e => e.value === formData.experience)?.label}</p>` : ''}
              ${formData.message ? `<p><strong>Messaggio:</strong><br>"${formData.message}"</p>` : ''}
            </div>
            
            <div style="background: #dbeafe; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #1e40af;">📋 Prossimi Passi:</h3>
              <ol>
                <li>Contatta il cliente entro 24 ore</li>
                <li>Valuta la richiesta con il team</li>
                <li>Fornisci consulenza specifica</li>
              </ol>
            </div>
            
            <p><strong>Data richiesta:</strong> ${new Date().toLocaleDateString('it-IT', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
          </div>
        `,
        text: `Nuova richiesta ${type} da ${formData.clientName} (${formData.clientEmail}). Controlla i dettagli nella dashboard admin.`
      };

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        throw new Error('Errore durante l\'invio della richiesta');
      }

      // Email di conferma al cliente
      const confirmationEmail = {
        to: formData.clientEmail,
        subject: `Conferma richiesta: ${type === 'auctions' ? 'Servizi Aste' : 'Compravendita'} - J-O-H`,
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #0d9488;">Grazie per la tua richiesta!</h2>
            
            <p>Ciao <strong>${formData.clientName}</strong>,</p>
            
            <p>Abbiamo ricevuto la tua richiesta per i nostri servizi di <strong>${type === 'auctions' ? 'aste immobiliari' : 'compravendita'}</strong>.</p>
            
            <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #0d9488;">Riepilogo della tua richiesta:</h3>
              <p><strong>Tipo richiesta:</strong> ${currentConfig.types.find(t => t.value === formData.requestType)?.label}</p>
              ${formData.propertyType ? `<p><strong>Tipo immobile:</strong> ${propertyTypes.find(p => p.value === formData.propertyType)?.label}</p>` : ''}
              ${formData.location ? `<p><strong>Zona:</strong> ${formData.location}</p>` : ''}
            </div>
            
            <p>Ti contatteremo entro <strong>24 ore</strong> per discutere in dettaglio le tue esigenze e fornirti la consulenza più appropriata.</p>
            
            <div style="background: #d1fae5; padding: 15px; border-radius: 8px; margin: 20px 0;">
              <p style="margin: 0;"><strong>Per domande urgenti, contattaci direttamente:</strong></p>
              <p style="margin: 5px 0 0 0;">
                📞 Telefono: +39 123 456 789<br>
                📧 Email: info@j-o-h.com<br>
                📱 WhatsApp: +39 333 123 4567
              </p>
            </div>
            
            <p>Grazie per aver scelto J-O-H per le tue esigenze immobiliari!</p>
            
            <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; margin-top: 30px; color: #6b7280; font-size: 14px;">
              <p>J-O-H - Servizi Immobiliari Premium<br>
              Email: info@j-o-h.com<br>
              Sito web: https://j-o-h.com</p>
            </div>
          </div>
        `,
        text: `Grazie ${formData.clientName}! Abbiamo ricevuto la tua richiesta per ${type === 'auctions' ? 'servizi aste' : 'compravendita'}. Ti contatteremo entro 24 ore.`
      };

      await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(confirmationEmail)
      });

      setSuccess(true);
      setTimeout(() => {
        onClose?.();
      }, 3000);

    } catch (err) {
      setError('Errore durante l\'invio della richiesta. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <FormContainer>
        <SuccessMessage>
          <h4>✅ {language === 'it' ? 'Richiesta inviata!' : 'Request sent!'}</h4>
          <p>
            {language === 'it' 
              ? 'Grazie per la tua richiesta. Ti contatteremo entro 24 ore per fornirti tutta l\'assistenza necessaria.'
              : 'Thank you for your request. We will contact you within 24 hours to provide all necessary assistance.'}
          </p>
          <WhatsAppInfo>
            <h5 style={{ color: '#25d366', marginBottom: '0.5rem' }}>
              {language === 'it' ? 'Per urgenze:' : 'For urgent matters:'}
            </h5>
            <a href="https://wa.me/+393331234567" target="_blank" rel="noopener noreferrer">
              📱 WhatsApp +39 333 123 4567
            </a>
          </WhatsAppInfo>
        </SuccessMessage>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormTitle>{currentConfig.title}</FormTitle>

      <InfoBox>
        <h4>
          {type === 'auctions' 
            ? (language === 'it' ? '🏛️ Servizi Aste Immobiliari' : '🏛️ Real Estate Auction Services')
            : (language === 'it' ? '🏠 Servizi Compravendita' : '🏠 Buy & Sell Services')
          }
        </h4>
        <p>
          {type === 'auctions'
            ? (language === 'it' 
              ? 'Il nostro team di esperti ti guida attraverso il complesso mondo delle aste immobiliari. Compila il form per ricevere consulenza personalizzata e assistenza completa.'
              : 'Our team of experts guides you through the complex world of real estate auctions. Fill out the form to receive personalized consulting and complete assistance.')
            : (language === 'it'
              ? 'Che tu stia cercando di comprare o vendere una proprietà, il nostro team è qui per aiutarti. Compila il form per ricevere consulenza professionale gratuita.'
              : 'Whether you are looking to buy or sell a property, our team is here to help. Fill out the form to receive free professional consulting.')
          }
        </p>
      </InfoBox>

      <form onSubmit={handleSubmit}>
        {/* Dati personali */}
        <FormGrid>
          <FormSection>
            <Label htmlFor="clientName">
              {language === 'it' ? 'Nome e Cognome *' : 'Full Name *'}
            </Label>
            <Input
              id="clientName"
              name="clientName"
              type="text"
              value={formData.clientName}
              onChange={handleInputChange}
              placeholder={language === 'it' ? 'Il tuo nome completo' : 'Your full name'}
              disabled={loading}
              required
            />
          </FormSection>

          <FormSection>
            <Label htmlFor="clientEmail">
              {language === 'it' ? 'Email *' : 'Email *'}
            </Label>
            <Input
              id="clientEmail"
              name="clientEmail"
              type="email"
              value={formData.clientEmail}
              onChange={handleInputChange}
              placeholder="email@example.com"
              disabled={loading}
              required
            />
          </FormSection>
        </FormGrid>

        <FormSection>
          <Label htmlFor="clientPhone">
            {language === 'it' ? 'Telefono' : 'Phone'}
          </Label>
          <Input
            id="clientPhone"
            name="clientPhone"
            type="tel"
            value={formData.clientPhone}
            onChange={handleInputChange}
            placeholder="+39 333 123 4567"
            disabled={loading}
          />
        </FormSection>

        {/* Tipo richiesta */}
        <FormSection>
          <Label htmlFor="requestType">
            {language === 'it' ? 'Tipo di richiesta *' : 'Request type *'}
          </Label>
          <Select
            id="requestType"
            name="requestType"
            value={formData.requestType}
            onChange={handleInputChange}
            disabled={loading}
            required
          >
            <option value="">
              {language === 'it' ? 'Seleziona...' : 'Select...'}
            </option>
            {currentConfig.types.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Select>
        </FormSection>

        {/* Dettagli immobile */}
        <FormGrid>
          <FormSection>
            <Label htmlFor="propertyType">
              {language === 'it' ? 'Tipo di immobile' : 'Property type'}
            </Label>
            <Select
              id="propertyType"
              name="propertyType"
              value={formData.propertyType}
              onChange={handleInputChange}
              disabled={loading}
            >
              <option value="">
                {language === 'it' ? 'Seleziona...' : 'Select...'}
              </option>
              {propertyTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          </FormSection>

          <FormSection>
            <Label htmlFor="location">
              {language === 'it' ? 'Zona di interesse' : 'Area of interest'}
            </Label>
            <Input
              id="location"
              name="location"
              type="text"
              value={formData.location}
              onChange={handleInputChange}
              placeholder={language === 'it' ? 'Es: Toscana, Pisa, Centro Storico' : 'E.g: Tuscany, Pisa, Historic Center'}
              disabled={loading}
            />
          </FormSection>
        </FormGrid>

        <FormGrid>
          <FormSection>
            <Label htmlFor="budget">
              {language === 'it' ? 'Budget indicativo (€)' : 'Indicative budget (€)'}
            </Label>
            <Input
              id="budget"
              name="budget"
              type="text"
              value={formData.budget}
              onChange={handleInputChange}
              placeholder={language === 'it' ? 'Es: 200.000 - 300.000' : 'E.g: 200,000 - 300,000'}
              disabled={loading}
            />
          </FormSection>

          <FormSection>
            <Label htmlFor="timeline">
              {language === 'it' ? 'Tempistiche' : 'Timeline'}
            </Label>
            <Select
              id="timeline"
              name="timeline"
              value={formData.timeline}
              onChange={handleInputChange}
              disabled={loading}
            >
              <option value="">
                {language === 'it' ? 'Seleziona...' : 'Select...'}
              </option>
              {timelines.map(timeline => (
                <option key={timeline.value} value={timeline.value}>
                  {timeline.label}
                </option>
              ))}
            </Select>
          </FormSection>
        </FormGrid>

        {/* Finanziamento */}
        <FormSection>
          <Label>
            {language === 'it' ? 'Tipo di finanziamento' : 'Type of financing'}
          </Label>
          <CheckboxContainer>
            {financingOptions.map(option => (
              <CheckboxItem key={option.value}>
                <input
                  type="checkbox"
                  checked={formData.financing.includes(option.value)}
                  onChange={() => handleFinancingChange(option.value)}
                  disabled={loading}
                />
                {option.label}
              </CheckboxItem>
            ))}
          </CheckboxContainer>
        </FormSection>

        {/* Esperienza */}
        <FormSection>
          <Label htmlFor="experience">
            {language === 'it' ? 'La tua esperienza nel settore' : 'Your experience in the sector'}
          </Label>
          <Select
            id="experience"
            name="experience"
            value={formData.experience}
            onChange={handleInputChange}
            disabled={loading}
          >
            <option value="">
              {language === 'it' ? 'Seleziona...' : 'Select...'}
            </option>
            {experiences.map(exp => (
              <option key={exp.value} value={exp.value}>
                {exp.label}
              </option>
            ))}
          </Select>
        </FormSection>

        {/* Messaggio */}
        <FormSection>
          <Label htmlFor="message">
            {language === 'it' ? 'Messaggio aggiuntivo' : 'Additional message'}
          </Label>
          <TextArea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder={language === 'it' 
              ? 'Descrivi le tue esigenze specifiche, domande particolari o altre informazioni utili...'
              : 'Describe your specific needs, particular questions or other useful information...'}
            disabled={loading}
          />
        </FormSection>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            style={{ flex: 1 }}
          >
            {loading ? (
              <>
                <Spinner />
                {language === 'it' ? 'Invio in corso...' : 'Sending...'}
              </>
            ) : (
              <>
                📩 {language === 'it' ? 'Invia Richiesta' : 'Send Request'}
              </>
            )}
          </Button>
          
          {onClose && (
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              {language === 'it' ? 'Annulla' : 'Cancel'}
            </Button>
          )}
        </ButtonGroup>
      </form>

      <WhatsAppInfo>
        <p>
          <strong>
            {language === 'it' ? 'Preferisci parlare direttamente?' : 'Prefer to speak directly?'}
          </strong>
        </p>
        <a href="https://wa.me/+393331234567" target="_blank" rel="noopener noreferrer">
          📱 {language === 'it' ? 'Contattaci su WhatsApp' : 'Contact us on WhatsApp'}
        </a>
      </WhatsAppInfo>
    </FormContainer>
  );
};

export default PropertyRequestForm; 